import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Heart, Users, Clock, Plus, Minus, Pencil, Copy, Trash2,
  CalendarPlus, AlertTriangle, ChefHat, Trophy, Check, Lightbulb, Crop, Loader2, X, Camera, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, recipeImage, fileUrl } from "@/lib/api";
import { categoryIcon, cuisineLabel } from "@/lib/constants";
import { CUISINES as CUISINE_META, cuisineOf } from "@/lib/cuisines";
import { adjustedTotalTime } from "@/lib/time";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AddMealDialog from "@/components/AddMealDialog";
import CookingMode from "@/components/CookingMode";
import DishCrop from "@/components/DishCrop";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useAuth } from "@/context/AuthContext";

const fmtQty = (q) => {
  if (q === null || q === undefined || q === "") return "";
  const r = Math.round(q * 100) / 100;
  return Number.isInteger(r) ? r : r.toFixed(2).replace(/\.?0+$/, "");
};

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(2);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [cooking, setCooking] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropMode, setCropMode] = useState("recrop"); // recrop | retake
  const [cropUrl, setCropUrl] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropBox, setCropBox] = useState({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
  const [cropLoading, setCropLoading] = useState(false);
  const [cropSaving, setCropSaving] = useState(false);
  const [retaking, setRetaking] = useState(false);
  const retakeRef = useRef(null);
  const [ids, setIds] = useState([]);
  const touchRef = useRef(null);
  const [cuisineOpen, setCuisineOpen] = useState(false);
  const [savingCuisine, setSavingCuisine] = useState(false);

  const saveCuisine = async (value) => {
    setSavingCuisine(true);
    try {
      await api.patch(`/recipes/${id}/cuisine`, { cuisine: value });
      setRecipe((r) => ({ ...r, cuisine: value }));
      setCuisineOpen(false);
      toast.success("Type de cuisine mis à jour");
    } catch {
      toast.error("Erreur");
    } finally {
      setSavingCuisine(false);
    }
  };

  const load = () => api.get(`/recipes/${id}`).then((r) => { setRecipe(r.data); setServings(user?.household_size || r.data.servings || 2); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/recipes").then((r) => setIds(r.data.map((x) => x.id))).catch(() => {}); }, []);

  useWakeLock(true); // garde l'écran allumé pendant la cuisine

  if (!recipe) return <div className="px-5 pt-10 text-center" style={{ color: "var(--at-muted)" }}>Chargement...</div>;

  const canEdit = user && (user.is_super_admin || (recipe.created_by === user.user_id && !recipe.created_by_is_admin));

  const idx = ids.indexOf(id);
  const hasPrev = idx > 0;
  const hasNext = idx >= 0 && idx < ids.length - 1;
  const goAdjacent = (dir) => {
    if (cropOpen) return;
    const ni = idx + dir;
    if (idx < 0 || ni < 0 || ni >= ids.length) return;
    navigate(`/recipes/${ids[ni]}`);
  };
  const onTouchStart = (e) => { const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY, target: e.target }; };
  const onTouchEnd = (e) => {
    const s = touchRef.current; touchRef.current = null;
    if (!s || cropOpen) return;
    if (s.target?.closest?.('[data-testid="recipe-photos-gallery"], [data-testid="dish-crop-editor"]')) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.8) {
      goAdjacent(dx < 0 ? 1 : -1); // swipe gauche => suivante, droite => précédente
    }
  };

  const openCropTool = async () => {
    if (!recipe.images?.length) { toast.error("Cette recette n'a pas d'image"); return; }
    setCropMode("recrop");
    setCropOpen(true);
    setCropLoading(true);
    const fallback = recipe.images[0];
    setCropSrc(fallback);
    setCropUrl(fileUrl(fallback));
    setCropBox({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
    try {
      const { data } = await api.post(`/recipes/${id}/detect-dish`);
      if (data.path) { setCropSrc(data.path); setCropUrl(fileUrl(data.path)); }
      const b = data.bbox;
      if (b && b.width) setCropBox({ x: b.x, y: b.y, width: b.width, height: b.height });
    } catch {
      toast.error("Détection automatique impossible, ajustez le cadre à la main");
    } finally {
      setCropLoading(false);
    }
  };

  const handleRetake = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setRetaking(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCropMode("retake");
      setCropSrc(res.data.path);
      setCropUrl(fileUrl(res.data.path));
      setCropBox({ x: 0, y: 0, width: 1, height: 1 });
      setCropLoading(false);
      setCropOpen(true);
    } catch {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setRetaking(false);
      if (retakeRef.current) retakeRef.current.value = "";
    }
  };

  const applyCrop = async () => {
    setCropSaving(true);
    try {
      const { data } = await api.post("/files/crop", { path: cropSrc, bbox: cropBox });
      await api.patch(`/recipes/${id}/image`, { path: data.path });
      toast.success("Photo du plat mise à jour");
      setCropOpen(false);
      load();
    } catch {
      toast.error("Impossible de mettre à jour la photo");
    } finally {
      setCropSaving(false);
    }
  };

  const factor = recipe.servings ? servings / recipe.servings : 1;
  const img = recipeImage(recipe);
  const time = adjustedTotalTime(recipe, servings);

  const mainIng = (recipe.ingredients || []).filter((i) => i.source_section !== "a_ajouter_vous_meme");
  const addIng = (recipe.ingredients || []).filter((i) => i.source_section === "a_ajouter_vous_meme");

  const toggleFav = async () => { await api.post(`/recipes/${id}/favorite`); load(); };
  const duplicate = async () => { const r = await api.post(`/recipes/${id}/duplicate`); toast.success("Recette dupliquée"); navigate(`/recipes/${r.data.id}`); };
  const remove = async () => { await api.delete(`/recipes/${id}`); toast.success("Recette supprimée"); navigate("/recipes"); };
  const quickToday = async (slot) => {
    const today = new Date();
    const d = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    await api.post("/meals", { recipe_id: id, date: d, slot, servings });
    toast.success(`Ajouté aujourd'hui (${slot})`);
  };
  const markCooked = async () => {
    await api.post("/history", { recipe_id: id, servings });
    toast.success("Bravo ! Ajouté à l'historique 🎉");
    load();
  };

  const IngRow = ({ ing }) => (
    <div className="flex items-center gap-3 py-2.5" data-testid={`detail-ing-${ing.normalized_name}`}>
      <span className="text-lg">{categoryIcon(ing.category)}</span>
      <div className="flex-1">
        <p className="font-medium capitalize">{ing.normalized_name || ing.original_name}</p>
        {ing.confidence !== undefined && ing.confidence < 0.7 && (
          <Badge className="mt-0.5 border-0 text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }}>
            <AlertTriangle size={10} className="mr-1" /> À vérifier
          </Badge>
        )}
      </div>
      <span className="font-semibold" style={{ color: "var(--at-brand)" }}>
        {ing.quantity !== null && ing.quantity !== undefined
          ? `${fmtQty(ing.quantity * factor)} ${ing.unit || ""}`.trim()
          : (ing.raw_quantity || ing.unit || "—")}
      </span>
    </div>
  );

  return (
    <div className="pb-6" data-testid="recipe-detail-page" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {hasPrev && (
        <button onClick={() => goAdjacent(-1)} data-testid="prev-recipe-btn" aria-label="Recette précédente"
          className="fixed left-1 top-1/2 z-30 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/80 backdrop-blur at-shadow at-press sm:flex"
          style={{ height: 44, width: 44, color: "var(--at-brand)" }}>
          <ChevronLeft size={24} />
        </button>
      )}
      {hasNext && (
        <button onClick={() => goAdjacent(1)} data-testid="next-recipe-btn" aria-label="Recette suivante"
          className="fixed right-1 top-1/2 z-30 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/80 backdrop-blur at-shadow at-press sm:flex"
          style={{ height: 44, width: 44, color: "var(--at-brand)" }}>
          <ChevronRight size={24} />
        </button>
      )}
      {/* Hero */}
      <div className="relative aspect-[4/3] w-full overflow-hidden sm:rounded-b-3xl" style={{ background: "var(--at-elevated)" }}>
        {img ? <img src={img} alt={recipe.title} className="h-full w-full object-contain" />
          : <div className="flex h-full items-center justify-center"><ChefHat size={56} style={{ color: "var(--at-muted)" }} /></div>}
        <div className="absolute inset-x-0 top-0 flex justify-between p-4">
          <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur at-shadow at-press"><ArrowLeft size={20} /></button>
          <button onClick={toggleFav} data-testid="detail-fav-btn" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur at-shadow at-press">
            <Heart size={20} fill={recipe.is_favorite ? "#E05A47" : "none"} color={recipe.is_favorite ? "#E05A47" : "#8C7B75"} />
          </button>
        </div>
        {canEdit && img && (
          <div className="absolute bottom-3 right-3 flex max-w-[calc(100%-1.5rem)] flex-wrap items-center justify-end gap-2">
            <button onClick={() => retakeRef.current?.click()} disabled={retaking} data-testid="retake-dish-btn"
              className="flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold backdrop-blur at-shadow at-press"
              style={{ color: "var(--at-brand)" }}>
              {retaking ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} Reprendre la photo
            </button>
            <button onClick={openCropTool} data-testid="recrop-dish-btn"
              className="flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold backdrop-blur at-shadow at-press"
              style={{ color: "var(--at-brand)" }}>
              <Crop size={15} /> Recadrer le plat
            </button>
          </div>
        )}
        <input ref={retakeRef} type="file" accept="image/*" className="hidden" data-testid="retake-input" onChange={(e) => handleRetake(e.target.files)} />
      </div>

      <div className="px-5">
        <div className="-mt-6 rounded-3xl border border-black/5 bg-white p-5 at-shadow-lg">
          {recipe.title_is_ai_generated && (
            <Badge className="mb-2 border-0" style={{ background: "var(--at-honey-light)", color: "#92400E" }}>Titre proposé par l'IA</Badge>
          )}
          <h1 className="font-display text-2xl font-bold leading-tight">{recipe.title}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm" style={{ color: "var(--at-text-2)" }}>
            <span className="flex items-center gap-1.5"><Users size={16} /> {recipe.servings} pers. (origine)</span>
            {time > 0 && <span className="flex items-center gap-1.5"><Clock size={16} /> {time} min</span>}
            {recipe.cuisine && recipe.cuisine !== "autre" ? (
              <button data-testid="cuisine-chip" disabled={!canEdit} onClick={() => canEdit && setCuisineOpen(true)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${canEdit ? "at-press" : ""}`}
                style={{ background: (cuisineOf(recipe.cuisine) || {}).bg || "var(--at-sage-light)", color: (cuisineOf(recipe.cuisine) || {}).color || "var(--at-sage)" }}>
                {cuisineLabel(recipe.cuisine)}{canEdit ? " ✎" : ""}
              </button>
            ) : canEdit ? (
              <button data-testid="cuisine-set-btn" onClick={() => setCuisineOpen(true)}
                className="rounded-full border border-dashed px-2.5 py-0.5 text-xs font-medium at-press" style={{ borderColor: "var(--at-border)", color: "var(--at-muted)" }}>
                + Type de cuisine
              </button>
            ) : null}
            {recipe.times_cooked > 0 && (
              <span className="flex items-center gap-1.5" style={{ color: "var(--at-honey)" }} data-testid="cooked-count">
                <Trophy size={16} /> Cuisiné {recipe.times_cooked}×
              </span>
            )}
          </div>
          {("wakeLock" in navigator) && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "var(--at-honey-light)", color: "#92400E" }} data-testid="wakelock-badge">
              <Lightbulb size={13} /> Écran gardé allumé pour cuisiner
            </div>
          )}
        </div>

        {/* Avertissement aversions */}
        {(recipe.disliked_main?.length > 0 || recipe.disliked_spice?.length > 0) && (
          <div className="mt-4 rounded-2xl border p-3 text-sm at-shadow" style={{ borderColor: "#F3C6BD", background: "#FBE7E3", color: "var(--at-brand)" }} data-testid="dislike-banner">
            {recipe.disliked_main?.length > 0 && (
              <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={15} /> Contient {recipe.disliked_main.join(", ")} que vous n'aimez pas</p>
            )}
            {recipe.disliked_spice?.length > 0 && (
              <p className="mt-1 text-xs" style={{ color: "var(--at-text-2)" }}>
                {recipe.disliked_spice.join(", ")} : retiré{recipe.disliked_spice.length > 1 ? "s" : ""} de votre liste de courses.
              </p>
            )}
          </div>
        )}

        {/* Ajuster portions */}
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 at-shadow" data-testid="servings-adjuster">
          <div>
            <p className="font-semibold">Ajuster les portions</p>
            <p className="text-xs" style={{ color: "var(--at-muted)" }}>Quantités recalculées automatiquement</p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-black/10 p-1">
            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" data-testid="detail-servings-minus" onClick={() => setServings((s) => Math.max(1, s - 1))}><Minus size={18} /></Button>
            <span className="w-8 text-center text-lg font-bold" data-testid="detail-servings-value">{servings}</span>
            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" data-testid="detail-servings-plus" onClick={() => setServings((s) => s + 1)}><Plus size={18} /></Button>
          </div>
        </div>

        {/* Ingrédients */}
        <section className="mt-5">
          <h2 className="mb-1 font-display text-xl font-semibold">Ingrédients</h2>
          <div className="divide-y divide-black/5 rounded-2xl border border-black/5 bg-white px-4 at-shadow">
            {mainIng.map((ing, i) => <IngRow key={i} ing={ing} />)}
          </div>
          {addIng.length > 0 && (
            <>
              <h3 className="mb-1 mt-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide" style={{ color: "var(--at-sage)" }}>
                À ajouter vous-même
              </h3>
              <div className="divide-y divide-black/5 rounded-2xl border bg-white px-4 at-shadow" style={{ borderColor: "var(--at-sage-light)" }}>
                {addIng.map((ing, i) => <IngRow key={i} ing={ing} />)}
              </div>
            </>
          )}
        </section>

        {/* Étapes */}
        {recipe.steps?.length > 0 && (
          <section className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">Préparation</h2>
              <Button data-testid="cooking-mode-btn" onClick={() => setCooking(true)} size="sm"
                className="rounded-full font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
                <ChefHat size={16} className="mr-1.5" /> Mode cuisine
              </Button>
            </div>
            <div className="space-y-3">
              {recipe.steps.map((s, i) => (
                <div key={i} className="rounded-2xl border border-black/5 bg-white p-4 at-shadow" data-testid={`detail-step-${i}`}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "var(--at-brand)" }}>{s.number || i + 1}</span>
                    {s.title && <span className="font-semibold">{s.title}</span>}
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--at-text-2)" }}>{s.instructions}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Ustensiles / allergènes / nutrition */}
        {recipe.utensils?.length > 0 && (
          <section className="mt-6">
            <h2 className="mb-2 font-display text-lg font-semibold">Ustensiles</h2>
            <div className="flex flex-wrap gap-2">
              {recipe.utensils.map((u, i) => <span key={i} className="rounded-full bg-white px-3 py-1.5 text-sm at-shadow border border-black/5">{u}</span>)}
            </div>
          </section>
        )}
        {recipe.allergens?.length > 0 && (
          <section className="mt-6">
            <h2 className="mb-2 font-display text-lg font-semibold">Allergènes</h2>
            <div className="flex flex-wrap gap-2">
              {recipe.allergens.map((a, i) => <span key={i} className="rounded-full px-3 py-1.5 text-sm font-medium" style={{ background: "#FEF3C7", color: "#92400E" }}>{a}</span>)}
            </div>
          </section>
        )}
        {recipe.nutrition && (recipe.nutrition.calories_kcal || recipe.nutrition.energy_kj || recipe.nutrition.protein_g) && (
          <section className="mt-6">
            <h2 className="mb-2 font-display text-lg font-semibold">Nutrition (par portion)</h2>
            <div className="grid grid-cols-4 gap-2">
              <div className="rounded-2xl border border-black/5 bg-white p-3 text-center at-shadow" data-testid="nut-energy">
                <p className="text-lg font-bold leading-tight">{recipe.nutrition.calories_kcal ?? "—"}<span className="text-xs font-semibold"> kcal</span></p>
                {recipe.nutrition.energy_kj ? <p className="text-xs font-medium leading-tight" style={{ color: "var(--at-text-2)" }}>{recipe.nutrition.energy_kj} kJ</p> : null}
                <p className="text-[10px] uppercase" style={{ color: "var(--at-muted)" }}>Énergie</p>
              </div>
              {[["protein_g", "Protéines"], ["carbohydrates_g", "Glucides"], ["fat_g", "Lipides"]].map(([k, lab]) => (
                <div key={k} className="rounded-2xl border border-black/5 bg-white p-3 text-center at-shadow">
                  <p className="text-lg font-bold">{recipe.nutrition[k] ?? "—"}</p>
                  <p className="text-[10px] uppercase" style={{ color: "var(--at-muted)" }}>{lab}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Actions */}
        <section className="mt-7 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="add-today-midi" onClick={() => quickToday("midi")} className="h-12 rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-honey)" }}>Aujourd'hui midi</Button>
            <Button data-testid="add-today-soir" onClick={() => quickToday("soir")} className="h-12 rounded-2xl font-semibold text-white at-press" style={{ background: "#4A4E7A" }}>Aujourd'hui soir</Button>
          </div>
          <Button data-testid="add-to-week-btn" onClick={() => setDlgOpen(true)} className="h-12 w-full rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
            <CalendarPlus size={18} className="mr-2" /> Ajouter à ma semaine
          </Button>
          <Button data-testid="mark-cooked-btn" onClick={markCooked} variant="outline" className="h-12 w-full rounded-2xl font-semibold at-press" style={{ borderColor: "var(--at-sage)", color: "var(--at-sage)" }}>
            <Check size={18} className="mr-2" /> J'ai cuisiné ce plat
          </Button>
          <div className={`grid gap-3 ${canEdit ? "grid-cols-3" : "grid-cols-1"}`}>
            {canEdit && (
              <Button data-testid="edit-btn" variant="outline" onClick={() => navigate(`/recipes/${id}/edit`)} className="h-11 rounded-2xl"><Pencil size={16} className="mr-1" /> Modifier</Button>
            )}
            <Button data-testid="duplicate-btn" variant="outline" onClick={duplicate} className="h-11 rounded-2xl"><Copy size={16} className="mr-1" /> Dupliquer</Button>
            {canEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="delete-btn" variant="outline" className="h-11 rounded-2xl text-red-500 border-red-200"><Trash2 size={16} className="mr-1" /> Suppr.</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer cette recette ?</AlertDialogTitle>
                    <AlertDialogDescription>Cette action est définitive et retirera aussi la recette de vos plannings.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirm-delete-btn" onClick={remove} className="rounded-xl bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </section>

        {/* Photos de la recette (hors photo du plat déjà affichée en haut) */}
        {recipe.images?.length > 1 && (
          <section className="mt-7" data-testid="recipe-photos-gallery">
            <h2 className="mb-2 font-display text-lg font-semibold">Photos de la recette</h2>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
              {recipe.images.slice(1).map((p, i) => (
                <img key={i} data-testid={`recipe-photo-${i}`} src={api.defaults.baseURL + "/files/" + p} alt="" className="h-40 w-auto max-w-[320px] shrink-0 rounded-2xl object-contain at-shadow" style={{ background: "var(--at-elevated)" }} />
              ))}
            </div>
          </section>
        )}
      </div>

      <AddMealDialog open={dlgOpen} onOpenChange={setDlgOpen} recipe={recipe} onAdded={() => toast.success("Ajouté à la semaine")} />
      {cooking && <CookingMode recipe={recipe} servings={servings} onClose={() => setCooking(false)} />}

      <Dialog open={cuisineOpen} onOpenChange={setCuisineOpen}>
        <DialogContent className="rounded-3xl" data-testid="cuisine-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Type de cuisine</DialogTitle>
            <DialogDescription>Corrigez le type si la détection s'est trompée.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {Object.entries(CUISINE_META).map(([key, c]) => {
              const active = recipe.cuisine === key;
              return (
                <button key={key} data-testid={`cuisine-opt-${key}`} disabled={savingCuisine} onClick={() => saveCuisine(key)}
                  className="rounded-full border px-3.5 py-2 text-sm font-semibold at-press"
                  style={active ? { background: c.color, borderColor: c.color, color: "#fff" } : { background: c.bg, borderColor: "transparent", color: c.color }}>
                  {c.label}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {cropOpen && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="recrop-modal">
          <div className="mt-auto rounded-t-3xl bg-white p-5 pb-8" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{cropMode === "retake" ? "Reprendre la photo du plat" : "Recadrer la photo du plat"}</h2>
              <button onClick={() => setCropOpen(false)} data-testid="recrop-close-btn" className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "var(--at-elevated)" }}><X size={18} /></button>
            </div>
            <p className="mb-3 text-xs" style={{ color: "var(--at-text-2)" }}>
              {cropLoading
                ? "L'IA détecte le plat…"
                : cropMode === "retake"
                  ? "Ajustez le cadre si besoin, puis enregistrez pour remplacer la photo."
                  : "Ajustez le cadre pour ne garder que l'assiette (déplacez le cadre, tirez les coins)."}
            </p>
            <div className="relative">
              {cropUrl && <DishCrop url={cropUrl} box={cropBox} onChange={setCropBox} />}
              {cropLoading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-2xl" style={{ background: "rgba(255,255,255,0.55)" }}>
                  <Loader2 size={28} className="animate-spin" style={{ color: "var(--at-brand)" }} />
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-3">
              <Button variant="outline" onClick={() => setCropOpen(false)} data-testid="recrop-cancel-btn" className="h-12 flex-1 rounded-2xl font-semibold">Annuler</Button>
              <Button onClick={applyCrop} disabled={cropSaving || cropLoading} data-testid="recrop-apply-btn"
                className="h-12 flex-1 rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
                {cropSaving ? <Loader2 size={18} className="animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
